import React, { Children, useState, useMemo, useCallback } from 'react';
import { Box, ListItem, Collapse, List, IconButton, Typography } from '@common-ui/ui';
import { ExpandMore, ExpandLess, Delete } from '@mui/icons-material';

import './styles.css';

function buildSidebarMenu(reportInfo) {
  const {
    images = [],
    image_options = {},
    options: { finalized } = {},
    data: { report_name } = {},
  } = reportInfo || {};

  const listOfImages = images.map((imageId) => ({
    id: imageId,
    imageId,
    name: image_options[imageId]?.name,
  }));

  const sidebarMenuItems = {
    header: {
      name: report_name,
      id: 'reportName',
    },
    navItems: [
      {
        name: 'Cover Page',
        id: 'reportCoverPage',
      },
      {
        name: 'Table of Contents',
        id: 'tableOfContents',
      },
      {
        name: 'List of Images',
        id: 'listOfImages',
        editable: finalized === false,
        subitems: listOfImages,
      },
    ],
  };

  return sidebarMenuItems;
}

function NavButtonWithIcon({ children, active, onClick, Icon, showIcon = false }) {
  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  return (
    <ListItem button className={active ? 'sidebar-navitem-active' : ''} onClick={handleClick}>
      <div className="sidebar-listitem">
        <span className="sidebar-navitem">{children}</span>
        {showIcon && <Icon />}
      </div>
    </ListItem>
  );
}

function NavListCollapsible({ children, open = false }) {
  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <List disablePadding sx={{ overflowY: 'auto', height: 'calc(100vh - 270px)' }}>
        {Children.map(children, (child, index) => (
          <ListItem
            key={`listitem_child_${index}_h`}
            disablePadding
            sx={{ paddingLeft: '4px', paddingRight: '4px', backgroundColor: 'grey.200' }}
          >
            {child}
          </ListItem>
        ))}
      </List>
    </Collapse>
  );
}

function ReportNavItem({ item, navIndex, onClick, onDelete, active = false }) {
  const { id, name, subitems, editable } = item;
  const [expand, setExpand] = useState(true);

  const handleOnClick = useCallback(() => {
    setExpand((prev) => !prev);
    onClick(id, navIndex);
  }, [id, navIndex, onClick]);

  const handleDelete = useCallback(
    (event, imageId) => {
      event.stopPropagation();
      if (imageId !== null) {
        onDelete(imageId);
      }
    },
    [onDelete, subitems],
  );

  const deleteIconStyling = {
    color: 'grey.500',
    '&:hover ': {
      color: 'grey.600',
      backgroundColor: 'transparent',
    },
  };

  return (
    <>
      <NavButtonWithIcon
        active={navIndex === active}
        onClick={subitems ? handleOnClick : () => onClick(id, navIndex)}
        Icon={() => (expand ? <ExpandLess /> : <ExpandMore />)}
        showIcon={subitems !== undefined}
      >
        <Typography variant="subtitle4">{name}</Typography>
      </NavButtonWithIcon>

      {subitems && (
        <NavListCollapsible open={expand}>
          {subitems?.map(({ id, name, imageId }, index) => (
            <NavButtonWithIcon
              key={`subitem_${index}_${imageId}`}
              onClick={() => onClick(id, navIndex)}
              Icon={() =>
                editable && (
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(event) => handleDelete(event, imageId)}
                    sx={deleteIconStyling}
                  >
                    <Delete />
                  </IconButton>
                )
              }
              showIcon={editable}
            >
              <Typography variant="subtitle2" sx={{ paddingLeft: 2 }}>
                {name}
              </Typography>
            </NavButtonWithIcon>
          ))}
        </NavListCollapsible>
      )}
    </>
  );
}

export default function Sidebar({ onClick, reportInfo, onDelete = null }) {
  const [activeItem, setActiveItem] = useState(0);

  const handleDeleteReportDetailPage = (imageId = '') => {
    onDelete?.(imageId);
  };

  const sidebarMenu = useMemo(() => {
    return buildSidebarMenu(reportInfo);
  }, [reportInfo]);

  const handleOnClick = (id, index) => {
    setActiveItem(index);
    if (typeof onClick === 'function') {
      onClick(id);
    }
  };

  return (
    <Box className="sidebar-container" backgroundColor="grey.300" id="reportSidebar">
      <List>
        <Typography variant="h4" style={{ paddingLeft: '14px' }}>
          {sidebarMenu?.header?.name}
        </Typography>

        {sidebarMenu?.navItems?.map((navItem, index) => (
          <ReportNavItem
            key={index}
            item={navItem}
            navIndex={index}
            onClick={handleOnClick}
            onDelete={handleDeleteReportDetailPage}
            active={activeItem}
          />
        ))}
      </List>
    </Box>
  );
}
